import React, { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, RotateCcw, RefreshCw, X } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import {
  API_ENV_TEST,
  getSelectedEnvironment,
} from '../../utils/apiEnvironment';
import {
  compareAppVersionRows,
  isLatestCandidateAtLeastMin,
} from '../../utils/compareAppVersionRows';
import DateTimePicker from '../../components/DateTimePicker/DateTimePicker';
import PaginatedTableCard from '../../components/PaginatedTableCard/PaginatedTableCard';
import { listAllAppVersions } from '../../api/appVersion';
import {
  PLATFORMS,
  listAppVersionPolicies,
  createAppVersionPolicy,
  updateAppVersionPolicy,
  deleteAppVersionPolicy,
  restoreAppVersionPolicy,
} from '../../api/appVersionPolicy';

const PAGE_SIZE = 10;
const APP_VERSION_POLICY_SKELETON_WIDTHS = [
  'w-10',
  'w-16',
  'w-20',
  'w-24',
  'w-24',
  'w-16',
  'w-16',
  'w-28',
  'w-28',
  'w-16 ml-auto',
];

function formatDateTime(value) {
  if (!value) return '-';
  const str = String(value);
  return str.replace('T', ' ').slice(0, 19);
}

function toDateTimeLocal(isoOrNull) {
  if (!isoOrNull) return '';
  const d = new Date(isoOrNull);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function fromDateTimeLocal(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function platformBadgeClasses(platform) {
  switch (platform) {
    case 'ANDROID':
      return 'bg-emerald-500 text-white';
    case 'IOS':
      return 'bg-white text-black';
    default:
      return 'bg-dark-surface text-dark-text-secondary';
  }
}

const PLATFORM_ICONS = {
  IOS: '/assets/app_store.png',
  ANDROID: '/assets/google_play_store.webp',
};

function PlatformBadge({ platform }) {
  const iconSrc = PLATFORM_ICONS[platform];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${platformBadgeClasses(platform)}`}
    >
      {iconSrc && (
        <img
          src={iconSrc}
          alt={`${platform} icon`}
          className="w-3.5 h-3.5 object-contain"
        />
      )}
      {platform}
    </span>
  );
}

function envBadgeClasses(env) {
  switch (env) {
    case 'PROD':
      return 'bg-primary/10 text-primary';
    case 'TEST':
      return 'bg-amber-500/10 text-amber-300';
    case 'DEV':
    default:
      return 'bg-dark-surface text-dark-text-secondary';
  }
}

const EMPTY_FORM = {
  platform: 'IOS',
  environment: 'PROD',
  min_version_id: '',
  latest_version_id: '',
  force_update: false,
  is_active: true,
  effective_from: toDateTimeLocal(new Date().toISOString()),
  effective_to: '',
  description: '',
  internal_note: '',
};

function toFormValues(row) {
  if (!row) return { ...EMPTY_FORM };
  return {
    platform: row.platform ?? 'IOS',
    environment: row.environment ?? 'PROD',
    min_version_id: row.min_version_id != null ? String(row.min_version_id) : '',
    latest_version_id: row.latest_version_id != null ? String(row.latest_version_id) : '',
    force_update: row.force_update === 1,
    is_active: row.is_active === 1,
    effective_from: toDateTimeLocal(row.effective_from),
    effective_to: toDateTimeLocal(row.effective_to),
    description: row.description ?? '',
    internal_note: row.internal_note ?? '',
  };
}

function toPayload(values) {
  return {
    platform: values.platform,
    environment: values.environment,
    min_version_id: values.min_version_id === '' ? null : Number(values.min_version_id),
    latest_version_id: values.latest_version_id === '' ? null : Number(values.latest_version_id),
    force_update: values.force_update ? 1 : 0,
    is_active: values.is_active ? 1 : 0,
    effective_from: fromDateTimeLocal(values.effective_from) ?? new Date().toISOString(),
    effective_to: fromDateTimeLocal(values.effective_to),
    description: values.description,
    internal_note: values.internal_note,
  };
}

export default function AppVersionPolicy() {
  const { user } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const queryClient = useQueryClient();

  const selectedApiEnv = getSelectedEnvironment();
  const lockedPolicyEnvironment = selectedApiEnv === API_ENV_TEST ? 'TEST' : 'PROD';
  const listFilterEnvironmentOptions = useMemo(
    () => [lockedPolicyEnvironment],
    [lockedPolicyEnvironment],
  );

  const [platform, setPlatform] = useState('ALL');
  const [environment, setEnvironment] = useState('ALL');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);

  const queryKey = useMemo(
    () => [
      'appVersionPolicies',
      apiEnvironment,
      { platform, environment, includeDeleted, activeOnly, search, page },
    ],
    [apiEnvironment, platform, environment, includeDeleted, activeOnly, search, page],
  );

  const listQuery = useQuery({
    queryKey,
    queryFn: () =>
      listAppVersionPolicies({
        platform,
        environment,
        includeDeleted,
        activeOnly,
        search,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const versionsQuery = useQuery({
    queryKey: ['allAppVersions', apiEnvironment],
    queryFn: () => listAllAppVersions(),
  });

  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data]);
  const versionsById = useMemo(() => {
    const map = new Map();
    versions.forEach((v) => map.set(v.id, v));
    return map;
  }, [versions]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['appVersionPolicies', apiEnvironment] });

  const createMutation = useMutation({
    mutationFn: (payload) => createAppVersionPolicy(payload, { actorUserId: user?.id }),
    onSuccess: () => {
      invalidate();
      closeModal();
    },
    onError: (err) => setFormError(err?.message || 'Failed to save'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) =>
      updateAppVersionPolicy(id, payload, { actorUserId: user?.id }),
    onSuccess: () => {
      invalidate();
      closeModal();
    },
    onError: (err) => setFormError(err?.message || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteAppVersionPolicy(id, { actorUserId: user?.id }),
    onSuccess: invalidate,
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => restoreAppVersionPolicy(id, { actorUserId: user?.id }),
    onSuccess: invalidate,
    onError: (err) => alert(err?.message || 'Failed to restore'),
  });

  useEffect(() => {
    setPage(1);
  }, [platform, environment, includeDeleted, activeOnly, search]);

  useEffect(() => {
    setEnvironment((prev) => {
      if (prev === 'ALL') return prev;
      return listFilterEnvironmentOptions.includes(prev) ? prev : 'ALL';
    });
  }, [apiEnvironment, listFilterEnvironmentOptions]);

  useEffect(() => {
    if (!modalOpen) return;
    setFormValues((v) =>
      v.environment === lockedPolicyEnvironment
        ? v
        : { ...v, environment: lockedPolicyEnvironment },
    );
  }, [modalOpen, lockedPolicyEnvironment, apiEnvironment]);

  useEffect(() => {
    if (!modalOpen) return;
    setFormValues((v) => {
      const minRow = v.min_version_id ? versionsById.get(Number(v.min_version_id)) : null;
      const latRow = v.latest_version_id ? versionsById.get(Number(v.latest_version_id)) : null;
      if (!minRow || !latRow) return v;
      const cmp = compareAppVersionRows(latRow, minRow);
      if (!Number.isNaN(cmp) && cmp < 0) {
        return { ...v, latest_version_id: '' };
      }
      return v;
    });
  }, [modalOpen, versionsById, formValues.min_version_id, formValues.latest_version_id]);

  const { items = [], total_count: totalCount = 0, page: currentPage = 1 } =
    listQuery.data || {};
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const openCreate = () => {
    setEditing(null);
    const env = getSelectedEnvironment() === API_ENV_TEST ? 'TEST' : 'PROD';
    setFormValues({
      ...EMPTY_FORM,
      environment: env,
      effective_from: toDateTimeLocal(new Date().toISOString()),
    });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const env = getSelectedEnvironment() === API_ENV_TEST ? 'TEST' : 'PROD';
    let next = { ...toFormValues(row), environment: env };
    const minRow = next.min_version_id ? versionsById.get(Number(next.min_version_id)) : null;
    const latRow = next.latest_version_id ? versionsById.get(Number(next.latest_version_id)) : null;
    if (minRow && latRow) {
      const cmp = compareAppVersionRows(latRow, minRow);
      if (!Number.isNaN(cmp) && cmp < 0) {
        next = { ...next, latest_version_id: '' };
      }
    }
    setFormValues(next);
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormError(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError(null);
    const lockedEnv = getSelectedEnvironment() === API_ENV_TEST ? 'TEST' : 'PROD';
    const values = { ...formValues, environment: lockedEnv };
    const minRow = values.min_version_id ? versionsById.get(Number(values.min_version_id)) : null;
    const latRow = values.latest_version_id ? versionsById.get(Number(values.latest_version_id)) : null;
    if (minRow && latRow) {
      const cmp = compareAppVersionRows(latRow, minRow);
      if (!Number.isNaN(cmp) && cmp < 0) {
        setFormError(t('appVersionPolicy.form.errorLatestBelowMin'));
        return;
      }
    }
    const payload = toPayload(values);
    if (editing) {
      updateMutation.mutate({ id: editing.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (row) => {
    const confirmMsg = t('appVersionPolicy.confirm.delete', {
      platform: row.platform,
      environment: row.environment,
    });
    if (!window.confirm(confirmMsg)) return;
    deleteMutation.mutate(row.id);
  };

  const handleRestore = (row) => {
    restoreMutation.mutate(row.id);
  };

  const versionLabel = (id) => {
    if (id == null) return '-';
    const v = versionsById.get(id);
    if (!v) return `#${id}`;
    return `${v.platform} ${v.version_name}${v.build_number ? ` (${v.build_number})` : ''}`;
  };

  const availableVersions = useMemo(
    () =>
      versions.filter(
        (v) => v.platform === formValues.platform && v.release_stage === 'RELEASED',
      ),
    [versions, formValues.platform],
  );

  const minVersionRow = useMemo(() => {
    if (!formValues.min_version_id) return null;
    return versionsById.get(Number(formValues.min_version_id)) ?? null;
  }, [formValues.min_version_id, versionsById]);

  const latestVersionOptions = useMemo(() => {
    if (!minVersionRow) return availableVersions;
    return availableVersions.filter((v) => isLatestCandidateAtLeastMin(v, minVersionRow));
  }, [availableVersions, minVersionRow]);

  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const effectiveFromDate = useMemo(
    () => (formValues.effective_from ? new Date(formValues.effective_from) : null),
    [formValues.effective_from],
  );

  const effectiveToMinDate = useMemo(() => {
    if (!effectiveFromDate) return todayStart;
    return effectiveFromDate > todayStart ? effectiveFromDate : todayStart;
  }, [effectiveFromDate, todayStart]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="min-h-screen py-8 px-3 sm:px-5 lg:px-6 bg-dark-bg">
      <div className="w-full max-w-[min(100%,1920px)] mx-auto">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-dark-text">
              {t('appVersionPolicy.title')}
            </h1>
            <p className="text-dark-text-secondary text-sm mt-1">
              {t('appVersionPolicy.description')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => listQuery.refetch()}
              disabled={listQuery.isRefetching}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dark-border text-dark-text hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
              title={t('appVersionPolicy.actions.refresh')}
            >
              <RefreshCw className={`w-4 h-4 ${listQuery.isRefetching ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">
                {t('appVersionPolicy.actions.refresh')}
              </span>
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-black text-sm font-medium hover:bg-highlight transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('appVersionPolicy.actions.add')}
            </button>
          </div>
        </div>

        <div className="bg-dark-card border border-dark-border rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3">
          <label className="text-xs text-dark-text-secondary flex items-center gap-2">
            <span>{t('appVersionPolicy.filter.platform')}</span>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="bg-dark-surface border border-dark-border text-xs text-dark-text-secondary rounded-md px-2 py-1"
            >
              <option value="ALL">{t('appVersionPolicy.filter.all')}</option>
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-dark-text-secondary flex items-center gap-2">
            <span>{t('appVersionPolicy.filter.environment')}</span>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              className="bg-dark-surface border border-dark-border text-xs text-dark-text-secondary rounded-md px-2 py-1"
            >
              <option value="ALL">{t('appVersionPolicy.filter.all')}</option>
              {listFilterEnvironmentOptions.map((env) => (
                <option key={env} value={env}>
                  {env}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-dark-text-secondary flex items-center gap-2">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              className="accent-primary"
            />
            <span>{t('appVersionPolicy.filter.activeOnly')}</span>
          </label>
          <label className="text-xs text-dark-text-secondary flex items-center gap-2 ml-auto">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              className="accent-primary"
            />
            <span>{t('appVersionPolicy.filter.showDeleted')}</span>
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('appVersionPolicy.filter.searchPlaceholder')}
            className="bg-dark-surface border border-dark-border text-xs text-dark-text placeholder-dark-text-muted rounded-md px-3 py-1.5 w-64"
          />
        </div>

        {listQuery.isError && (
          <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {listQuery.error?.message || 'Failed to load policies'}
          </div>
        )}

        <PaginatedTableCard
          error={listQuery.error}
          dataUpdatedAt={listQuery.dataUpdatedAt}
          isPending={listQuery.isPending}
          isFetching={listQuery.isFetching}
          isEmpty={!listQuery.isPending && items.length === 0}
          skeletonRows={PAGE_SIZE}
          skeletonCellWidths={APP_VERSION_POLICY_SKELETON_WIDTHS}
          colSpan={10}
          minHeight="min-h-[28.5rem]"
          tableClassName="w-full text-sm"
          loadingLabel={t('common.loading')}
          emptyLabel={t('appVersionPolicy.table.empty')}
          page={page}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setPage}
          prevLabel={t('user.pagination.prev')}
          nextLabel={t('user.pagination.next')}
          summary={t('user.pagination.page', { page: currentPage, totalPages })}
          header={(
            <tr className="border-b border-dark-border text-xs text-dark-text-secondary">
              <th className="text-left py-3 px-4">ID</th>
              <th className="text-left py-3 px-4">{t('appVersionPolicy.table.platform')}</th>
              <th className="text-left py-3 px-4">{t('appVersionPolicy.table.environment')}</th>
              <th className="text-left py-3 px-4">{t('appVersionPolicy.table.minVersion')}</th>
              <th className="text-left py-3 px-4">{t('appVersionPolicy.table.latestVersion')}</th>
              <th className="text-left py-3 px-4">{t('appVersionPolicy.table.forceUpdate')}</th>
              <th className="text-left py-3 px-4">{t('appVersionPolicy.table.active')}</th>
              <th className="text-left py-3 px-4">{t('appVersionPolicy.table.effectiveFrom')}</th>
              <th className="text-left py-3 px-4">{t('appVersionPolicy.table.effectiveTo')}</th>
              <th className="text-right py-3 px-4">{t('appVersionPolicy.table.actions')}</th>
            </tr>
          )}
        >
          {items.map((row) => {
            const isDeleted = row.deleted_at != null;
            return (
              <tr
                key={row.id}
                className={[
                  'border-b border-dark-border last:border-0 hover:bg-dark-surface/50',
                  isDeleted ? 'opacity-60' : '',
                ].join(' ')}
              >
                <td className="py-3 px-4 text-dark-text-secondary text-xs">{row.id}</td>
                <td className="py-3 px-4">
                  <PlatformBadge platform={row.platform} />
                </td>
                <td className="py-3 px-4">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${envBadgeClasses(row.environment)}`}
                  >
                    {row.environment}
                  </span>
                </td>
                <td className="py-3 px-4 text-dark-text-secondary text-xs">
                  {versionLabel(row.min_version_id)}
                </td>
                <td className="py-3 px-4 text-dark-text-secondary text-xs">
                  {versionLabel(row.latest_version_id)}
                </td>
                <td className="py-3 px-4">
                  {row.force_update === 1 ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500 text-white">
                      {t('appVersionPolicy.badge.forceOn')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500 text-white">
                      {t('appVersionPolicy.badge.forceOff')}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4">
                  {row.is_active === 1 ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-300">
                      {t('appVersionPolicy.badge.active')}
                    </span>
                  ) : (
                    <span className="text-xs text-dark-text-muted">
                      {t('appVersionPolicy.badge.inactive')}
                    </span>
                  )}
                  {isDeleted && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400">
                      {t('appVersionPolicy.badge.deleted')}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-dark-text-secondary text-xs">
                  {formatDateTime(row.effective_from)}
                </td>
                <td className="py-3 px-4 text-dark-text-secondary text-xs">
                  {formatDateTime(row.effective_to)}
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex items-center gap-1">
                    {isDeleted ? (
                      <button
                        type="button"
                        onClick={() => handleRestore(row)}
                        className="p-1.5 rounded border border-dark-border text-dark-text-secondary hover:text-primary hover:border-primary transition-colors"
                        title={t('appVersionPolicy.actions.restore')}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="p-1.5 rounded border border-dark-border text-dark-text-secondary hover:text-primary hover:border-primary transition-colors"
                          title={t('appVersionPolicy.actions.edit')}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          className="p-1.5 rounded border border-dark-border text-dark-text-secondary hover:text-red-400 hover:border-red-400 transition-colors"
                          title={t('appVersionPolicy.actions.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </PaginatedTableCard>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-dark-card border border-dark-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-5 py-4 border-b border-dark-border">
              <h2 className="text-lg font-semibold text-dark-text">
                {editing
                  ? t('appVersionPolicy.modal.editTitle', {
                      platform: editing.platform,
                      environment: editing.environment,
                    })
                  : t('appVersionPolicy.modal.createTitle')}
              </h2>
              <button
                type="button"
                className="text-dark-text-secondary hover:text-primary"
                onClick={closeModal}
                aria-label="close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={t('appVersionPolicy.form.platform')} required>
                  <select
                    value={formValues.platform}
                    onChange={(e) =>
                      setFormValues((v) => ({
                        ...v,
                        platform: e.target.value,
                        min_version_id: '',
                        latest_version_id: '',
                      }))
                    }
                    className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                  >
                    {PLATFORMS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t('appVersionPolicy.form.environment')} required>
                  <select
                    value={lockedPolicyEnvironment}
                    disabled
                    className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text disabled:opacity-70"
                  >
                    <option value={lockedPolicyEnvironment}>{lockedPolicyEnvironment}</option>
                  </select>
                </Field>
                <Field
                  label={t('appVersionPolicy.form.minVersion')}
                  hint={t('appVersionPolicy.form.minVersionHint')}
                >
                  <select
                    value={formValues.min_version_id}
                    onChange={(e) => {
                      const nextMin = e.target.value;
                      setFormValues((v) => {
                        const next = { ...v, min_version_id: nextMin };
                        if (!nextMin || !v.latest_version_id) return next;
                        const minRow = versionsById.get(Number(nextMin));
                        const latRow = versionsById.get(Number(v.latest_version_id));
                        if (minRow && latRow) {
                          const cmp = compareAppVersionRows(latRow, minRow);
                          if (!Number.isNaN(cmp) && cmp < 0) {
                            next.latest_version_id = '';
                          }
                        }
                        return next;
                      });
                    }}
                    className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                  >
                    <option value="">{t('appVersionPolicy.form.none')}</option>
                    {availableVersions.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.version_name}
                        {v.build_number ? ` (${v.build_number})` : ''} · {v.release_stage}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label={t('appVersionPolicy.form.latestVersion')}
                  hint={t('appVersionPolicy.form.latestVersionHint')}
                >
                  <select
                    value={formValues.latest_version_id}
                    onChange={(e) =>
                      setFormValues((v) => ({ ...v, latest_version_id: e.target.value }))
                    }
                    className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                  >
                    <option value="">{t('appVersionPolicy.form.none')}</option>
                    {latestVersionOptions.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.version_name}
                        {v.build_number ? ` (${v.build_number})` : ''} · {v.release_stage}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t('appVersionPolicy.form.effectiveFrom')} required>
                  <DateTimePicker
                    value={formValues.effective_from}
                    onChange={(next) =>
                      setFormValues((v) => ({ ...v, effective_from: next }))
                    }
                    clearable={false}
                    required
                  />
                </Field>
                <Field label={t('appVersionPolicy.form.effectiveTo')}>
                  <DateTimePicker
                    value={formValues.effective_to}
                    onChange={(next) =>
                      setFormValues((v) => ({ ...v, effective_to: next }))
                    }
                    minDate={effectiveToMinDate}
                  />
                </Field>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-xs text-dark-text-secondary">
                  <input
                    type="checkbox"
                    checked={formValues.force_update}
                    onChange={(e) =>
                      setFormValues((v) => ({ ...v, force_update: e.target.checked }))
                    }
                    className="accent-primary"
                  />
                  <span>{t('appVersionPolicy.form.forceUpdate')}</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-dark-text-secondary">
                  <input
                    type="checkbox"
                    checked={formValues.is_active}
                    onChange={(e) =>
                      setFormValues((v) => ({ ...v, is_active: e.target.checked }))
                    }
                    className="accent-primary"
                  />
                  <span>{t('appVersionPolicy.form.isActive')}</span>
                </label>
              </div>

              <Field label={t('appVersionPolicy.form.description')}>
                <textarea
                  value={formValues.description}
                  onChange={(e) =>
                    setFormValues((v) => ({ ...v, description: e.target.value }))
                  }
                  rows={3}
                  className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                />
              </Field>

              <Field label={t('appVersionPolicy.form.internalNote')}>
                <textarea
                  value={formValues.internal_note}
                  onChange={(e) =>
                    setFormValues((v) => ({ ...v, internal_note: e.target.value }))
                  }
                  rows={3}
                  className="w-full bg-dark-surface border border-dark-border rounded-md px-3 py-2 text-dark-text"
                />
              </Field>

              {formError && (
                <div className="p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                  {formError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-dark-border -mx-5 px-5">
                <button
                  type="button"
                  className="px-4 py-2 rounded-md border border-dark-border text-dark-text-secondary hover:text-primary hover:border-primary transition-colors text-sm"
                  onClick={closeModal}
                >
                  {t('appVersionPolicy.form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 rounded-md bg-primary text-black text-sm font-medium hover:bg-highlight transition-colors disabled:opacity-50"
                >
                  {isSaving ? t('common.loading') : t('appVersionPolicy.form.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-dark-text-secondary">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="text-[10px] text-dark-text-muted">{hint}</span>}
    </label>
  );
}
